import { debounce } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MainHeader from '../../baseComponents/mainHeader';
import PageEditor from '../../baseComponents/PageEditor';
import { actions } from '../../redux';
import { eventBus, getUpdatedViewPortList, updateCanvasSize, ZOOM_LIMIT, BUS_EVENTS } from '../../utils';
import LeftSide from './components/LeftSide';
import RightSide from './components/RightSide';
import './styles.scss';

const ZOOM_SPEED = 0.99; // from 0 -> 1

const Editor = () => {
  const dispatch = useDispatch();
  const ref = useRef(null);
  const { userSettings } = useSelector((state) => state.app);
  const isExpandMenuRight = useSelector((state) => state?.board?.activeType);
  const dispatchViewPort = debounce(() => {
    const viewPortList = getUpdatedViewPortList(userSettings);
    dispatch(actions.setUserSettings({ viewPortList: viewPortList }));
  }, 100);
  const calcViewportOnScroll = (deltaX, deltaY) => {
    const [scaleX, skewX, skewY, scaleY, translationX, translationY] = window.canvas.viewportTransform;
    let [newX, newY] = [deltaX, deltaY];
    try {
      const { tl, br } = window.canvas.calcViewportBoundaries() || {};
      const viewBox = {
        top: tl.y,
        left: tl.x,
        bottom: br.y,
        right: br.x,
        width: br.x - tl.x,
        height: br.y - tl.y,
      };
      const { top, left, width, height } = window.canvas.backgroundImage;
      const [right, bottom] = [left + width, top + height];
      const padding = 20 / scaleX;
      const paddingHorizontal = viewBox.width - width - padding;
      const paddingVertical = viewBox.height - height - padding;

      const maxTop = top - padding;
      const maxLeft = left - padding;
      const minRight = right + padding;
      const minBottom = bottom + padding;

      const minTop = top - paddingVertical;
      const minLeft = left - paddingHorizontal;
      const maxRight = right + paddingHorizontal;
      const maxBottom = bottom + paddingVertical;

      let xChange = deltaX / scaleX;
      let yChange = deltaY / scaleY;

      if (deltaX < 0) {
        xChange = Math.max(xChange, Math.min(0, maxLeft - viewBox.left, minLeft - viewBox.left));
        newX = Math.round(xChange * scaleX);
      }
      if (deltaX > 0) {
        xChange = Math.min(xChange, Math.max(0, minRight - viewBox.right, maxRight - viewBox.right));
        newX = Math.round(xChange * scaleX);
      }
      if (deltaY < 0) {
        yChange = Math.max(yChange, Math.min(maxTop - viewBox.top, minTop - viewBox.top, 0));
        newY = Math.round(yChange * scaleY);
      }
      if (deltaY > 0) {
        yChange = Math.min(yChange, Math.max(minBottom - viewBox.bottom, maxBottom - viewBox.bottom, 0));
        newY = Math.round(yChange * scaleY);
      }
    } catch (error) {
      console.error(error);
    }
    if (newX !== 0 || newY !== 0) {
      window.canvas.setViewportTransform([scaleX, skewX, skewY, scaleY, translationX - newX, translationY - newY]);
      dispatchViewPort();
    }
  };

  const onWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { ctrlKey, shiftKey, deltaX, deltaY } = e;
    let delta;
    if (ctrlKey) {
      // smooth zoom: -10 <= delta <= 10
      delta = Math.max(Math.min(deltaY, 10), -10);
      let canvasZoom = window.canvas.getZoom();
      canvasZoom *= ZOOM_SPEED ** delta;
      canvasZoom = Math.max(Math.min(canvasZoom, ZOOM_LIMIT.MAX), ZOOM_LIMIT.MIN);
      const zoom = canvasZoom * 100;
      eventBus.emit(BUS_EVENTS.ZOOM, zoom, e, true);
      dispatchViewPort();
      return;
    }

    if (shiftKey) {
      // scroll horizotal
      delta = Math.max(Math.min(deltaY + deltaX, 50), -50);
      calcViewportOnScroll(delta, 0);
    } else {
      // scroll vertical or touchpad scroll
      calcViewportOnScroll(deltaX, deltaY);
    }
    eventBus.emit(BUS_EVENTS.UPDATE_POSITION_MENU_EDITOR);
  };
  window.onresize = function() {
    updateCanvasSize(window.canvas);
  };
  useEffect(() => {
    ref.current && ref.current.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      ref.current && ref.current.removeEventListener('wheel', onWheel, { passive: false });
    };
  }, [ref.current, userSettings]);
  useEffect(() => {
    eventBus.on('DISPATCH_VIEW_PORT', dispatchViewPort);
    return () => {
      eventBus.off('DISPATCH_VIEW_PORT', dispatchViewPort);
    };
  }, [userSettings]);
  return (
    <div className="mo-landing">
      <LeftSide />
      <div className={`main-board ${isExpandMenuRight ? '_has-menu-right' : ''}`} id="mo-main-board">
        <MainHeader />
        <div className="editor-area" style={{ marginTop: '40px' }} ref={ref}>
          <PageEditor />
        </div>
      </div>
      <RightSide />
    </div>
  );
};

export default Editor;
